import React from 'react'
import { Routes, Route } from 'react-router-dom'
import CssBaseline from '@mui/material/CssBaseline'
import Bugsnag from '@bugsnag/js'
import BugsnagPluginReact from '@bugsnag/plugin-react'

import Login from 'Components/connectedComponents/login'
import { ForgotPassword } from 'Containers/shared/forgotpassword'
import { Header } from 'Containers/header'
import { Contracts, Files } from 'Containers/inquiries'
import {
  Contracts as CandidatesContracts,
  Files as CandidatesFiles,
} from 'Containers/candidates'
import { ContractForm } from 'Components/connectedComponents/contracts'
import {
  Destinations,
  ManageUsers,
  SSHKeyManagement,
  ZipCodes,
} from 'Containers/configuration'
import {
  InstitutionSettings
} from 'Components/connectedComponents/configuration'
import { Explore } from 'Containers/shared/segmentdisplay'
import { SegmentQueue } from 'Containers/admin/segmentqueue'
import { OrangeAnalysis } from 'Components/connectedComponents/admin'
import { ActiveSegments } from 'Containers/admin/hipLeads'
import { LeadFileStatus } from 'Containers/admin/leadfilestatus'
import { SessionExpirationWarning } from 'Components/connectedComponents/ui/SessionExpirationWarning'
import { SnackbarContainer } from 'Containers/shared/snackbar/Snackbar'
import { CheckPermissions } from 'Containers/shared/permissions'
import { Permissions } from 'Configs/userPermissions'
import { Providers } from 'Utils/Providers'
import { Router } from 'Utils/router'
import { VersionInfo } from 'Components/shared/version'
import { ContractType, ContractSource } from 'Types/index'
import { AccountSettingsPage } from 'Components/connectedComponents/account/AccountSettingsPage'
import { DeletionUpload } from 'Components/connectedComponents/admin/DeletionUpload'
import { Deletions } from 'Components/connectedComponents/information/Deletions'
import { Status as AarPartnerDataStatus } from 'Containers/admin/aarPartnerData'

Bugsnag.start({
  apiKey: `${process.env.REACT_APP_BUGSNAG_API_KEY}`,
  plugins: [new BugsnagPluginReact()],
  releaseStage: process.env.NODE_ENV,
  enabledReleaseStages: ['production', 'staging', 'development'],
})

const ErrorBoundary = Bugsnag.getPlugin('react').createErrorBoundary(React)

const InquiriesRoutes = () => {
  return (
    <Routes>
      <Route index element={<Contracts />} />
      <Route path='contracts' element={<Contracts />} />
      <Route path='contracts/segment/:segmentId' element={<Contracts />} />
      <Route path='files' element={<Files />} />
      <Route
        path='contracts/:contractId'
        element={
          <ContractForm
            contractSource={ContractSource.inquiries}
            contractType={ContractType.annual}
          />
        }
      />
    </Routes>
  )
}
const CandidatesRoutes = () => {
  return (
    <Routes>
      <Route index element={<CandidatesContracts />} />
      <Route path='contracts' element={<CandidatesContracts />} />
      <Route
        path='contracts/segment/:segmentId'
        element={<CandidatesContracts />}
      />
      <Route path='files' element={<CandidatesFiles />} />
      <Route
        path='contracts/:contractId'
        element={
          <ContractForm
            contractSource={ContractSource.candidates}
            contractType={ContractType.on_demand}
          />
        }
      />
      <Route path='explore' element={<Explore />} />
    </Routes>
  )
}

const ConfigurationRoutes = () => {
  return (
    <Routes>
      <Route index element={<Destinations />} />
      <Route path='destinations' element={<Destinations />} />
      <Route path='manageusers' element={<ManageUsers />} />
      <Route path='managekeys' element={<SSHKeyManagement />} />
      <Route path='zipcodes' element={<ZipCodes />} />
      <Route path='institution-settings' element={<InstitutionSettings />} />
      <Route path='account-settings' element={<AccountSettingsPage />} />
    </Routes>
  )
}

const InformationRoutes = () => {
  return (
    <Routes>
      <Route index element={<Deletions />} />
      <Route path='deletions' element={<Deletions />} />
    </Routes>
  )
}

const AarPartnerDataRoutes = () => {
  return (
    <Routes>
      <Route index element={<OrangeAnalysis />} />
      <Route path='upload' element={<OrangeAnalysis />} />
      <Route path='status' element={<AarPartnerDataStatus />} />
    </Routes>
  )
}

const AdminRoutes = () => {
  return (
    <Routes>
      <Route index element={<SegmentQueue />} />
      <Route path='segments-queue' element={<SegmentQueue />} />
      <Route path='lead-file-status' element={<LeadFileStatus />} />
      <Route path='hip-leads' element={<ActiveSegments />} />
      <Route path='deletion-upload' element={<DeletionUpload />} />
      <Route path='aar-partner-data/*' element={<AarPartnerDataRoutes />} />
    </Routes>
  )
}

export const App = () => {
  return (
    <ErrorBoundary>
      <Providers>
        <CssBaseline />
        <SnackbarContainer />
        <SessionExpirationWarning />
        <Router>
          <Routes>
            <Route path='/' element={<Login />} />
            <Route path='/forgot' element={<ForgotPassword />} />
            <Route element={<Header />}>
              <Route path='inquiries/*' element={<InquiriesRoutes />} />
              <Route
                path='institution/:ipedId/inquiries/*'
                element={<InquiriesRoutes />}
              />
              <Route
                path='institution/:ipedId/prospects/*'
                element={<CandidatesRoutes />}
              />
              <Route
                path='institution/:ipedId/configuration/*'
                element={<ConfigurationRoutes />}
              />
              <Route
                path='institution/:ipedId/information/*'
                element={<InformationRoutes />}
              />
              <Route
                path='admin/*'
                element={<>
                  <CheckPermissions permission={Permissions.SwitchInstitution}>
                    <AdminRoutes />
                  </CheckPermissions>
                  </>
                }
              />
            </Route>
          </Routes>
        </Router>
      </Providers>
      <VersionInfo />
    </ErrorBoundary>
  )
}
